
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Trash2, Plus, Download, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Local storage key for backup
const SOFT_BLOCK_WORDS_KEY = 'fomo_soft_block_words';

// Emoji to replace soft blocked words
const DOLPHIN_EMOJI = '🐬';

interface SoftBlockWord {
  id: string;
  word: string;
  created_at: string;
  category: string;
}

export const SoftBlockWordsManager = () => {
  const [softBlockWords, setSoftBlockWords] = useState<SoftBlockWord[]>([]);
  const [newWord, setNewWord] = useState('');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Use react-query to manage database state
  const queryClient = useQueryClient();
  
  const { data: words, isLoading, error } = useQuery({
    queryKey: ['soft-block-words'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('soft_block_words')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data as SoftBlockWord[];
    }
  });
  
  // Set words when they load from the query
  useEffect(() => {
    if (words) {
      setSoftBlockWords(words);
    }
  }, [words]);
  
  // Create the table if it doesn't exist (only needed first time)
  useEffect(() => {
    createSoftBlockWordsTableIfNeeded();
  }, []);
  
  const createSoftBlockWordsTableIfNeeded = async () => {
    try {
      const { count, error } = await supabase
        .from('soft_block_words')
        .select('*', { count: 'exact', head: true });
      
      if (error) {
        // Table might not exist, try to create it
        await createTable();
        await addDefaultWords();
      } else if (count === 0) {
        // Table exists but is empty, add default words
        await addDefaultWords();
      }
    } catch (err) {
      console.error('Error checking/creating soft block words table:', err);
    }
  };
  
  const createTable = async () => {
    try {
      // Use our function to create the table
      const { error } = await supabase.rpc('create_soft_block_words_table', {});
      
      if (error) {
        console.error('Error creating soft block words table:', error);
        toast.error('Failed to initialize soft block words system');
      }
    } catch (error) {
      console.error('Error creating soft block words table:', error);
    }
  };
  
  const addDefaultWords = async () => {
    const defaultWords = [
      { word: 'bitch', category: 'profanity' },
      { word: 'fuck', category: 'profanity' },
      { word: 'shit', category: 'profanity' },
      { word: 'ass', category: 'profanity' },
      { word: 'cunt', category: 'profanity' }
    ];
    
    try {
      // Add default words to the database
      const { error } = await supabase
        .from('soft_block_words')
        .insert(defaultWords);
      
      if (error && error.code !== '23505') { // Ignore duplicate error
        console.error('Error adding default soft block words:', error);
      } else {
        // Refresh data
        queryClient.invalidateQueries({ queryKey: ['soft-block-words'] });
      }
    } catch (error) {
      console.error('Error adding default soft block words:', error);
    }
  };

  const handleAddWord = async () => {
    if (!newWord.trim()) {
      toast.error('Please enter a word');
      return;
    }
    
    const wordToAdd = newWord.toLowerCase().trim();
    
    // Check if word already exists
    const exists = softBlockWords.some(
      (item) => item.word.toLowerCase() === wordToAdd
    );
    
    if (exists) {
      toast.error('This word is already in the list');
      return;
    }

    const newSoftBlockWord = {
      word: wordToAdd,
      category: 'profanity', // Default category
    };

    try {
      // Add to database
      const { error } = await supabase
        .from('soft_block_words')
        .insert([newSoftBlockWord]);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      queryClient.invalidateQueries({ queryKey: ['soft-block-words'] });
      
      toast.success(`Word added to soft block list (will be replaced with ${DOLPHIN_EMOJI})`);
      setNewWord('');
    } catch (error) {
      console.error('Error adding soft block word:', error);
      toast.error('Failed to add word');
    }
  };

  const handleDeleteWord = async (id: string) => {
    setIsDeleting(id);
    
    try {
      // Delete from database
      const { error } = await supabase
        .from('soft_block_words')
        .delete()
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      const updatedWords = softBlockWords.filter(word => word.id !== id);
      setSoftBlockWords(updatedWords);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['soft-block-words'] });
      
      toast.success('Word removed from soft block list');
    } catch (error) {
      console.error('Error deleting soft block word:', error);
      toast.error('Failed to delete word');
    } finally {
      setIsDeleting(null);
    }
  };
  
  const exportWords = async () => {
    try {
      // Get the latest data from the database to ensure we export the most current list
      const { data, error } = await supabase
        .from('soft_block_words')
        .select('*');
        
      if (error) {
        throw error;
      }
      
      const wordListData = JSON.stringify(data, null, 2);
      const blob = new Blob([wordListData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fomo-soft-block-words.json';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Soft block word list exported successfully');
    } catch (error) {
      console.error('Error exporting words:', error);
      toast.error('Failed to export word list');
    }
  };
  
  const importWords = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        const importedWords = JSON.parse(content) as SoftBlockWord[];
        
        // Validate the imported data structure
        if (!Array.isArray(importedWords) || 
            !importedWords.every(word => 
              typeof word.word === 'string' && 
              typeof word.category === 'string')) {
          throw new Error('Invalid format');
        }
        
        // Prepare words for insert/upsert - remove IDs and timestamps that might conflict
        const wordsToUpsert = importedWords.map(({ word, category }) => ({
          word,
          category
        }));
        
        // Delete existing words first (full replacement)
        const { error: deleteError } = await supabase
          .from('soft_block_words')
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all
          
        if (deleteError) {
          throw deleteError;
        }
        
        // Insert new words
        const { error: insertError } = await supabase
          .from('soft_block_words')
          .insert(wordsToUpsert);
          
        if (insertError) {
          throw insertError;
        }
        
        // Refresh the data
        queryClient.invalidateQueries({ queryKey: ['soft-block-words'] });
        
        toast.success('Words imported successfully to database');
      } catch (error) {
        console.error('Error importing words:', error);
        toast.error('Failed to import words. Please check file format.');
      }
    };
    reader.readAsText(file);
    
    // Reset the input element
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        <Input
          placeholder="Add a word to soft block (will become 🐬)"
          value={newWord}
          onChange={(e) => setNewWord(e.target.value)}
          className="max-w-sm"
        />
        <Button 
          onClick={handleAddWord} 
          disabled={!newWord.trim()}
          className="gap-1"
        >
          <Plus size={16} />
          Add Word
        </Button>
        
        <div className="flex ml-auto gap-2">
          <Button
            variant="outline"
            onClick={exportWords}
            title="Export soft block word list"
            className="gap-1"
          >
            <Download size={16} />
            Export
          </Button>
          
          <Button
            variant="outline"
            className="gap-1"
            onClick={() => document.getElementById('import-soft-block-words')?.click()}
            title="Import soft block word list"
          >
            <Upload size={16} />
            Import
          </Button>
          <input
            type="file"
            id="import-soft-block-words"
            onChange={importWords}
            accept=".json"
            className="hidden"
          />
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Word (will be replaced with {DOLPHIN_EMOJI})</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Added On</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  Loading word list...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-red-500">
                  Error loading words. Please refresh.
                </TableCell>
              </TableRow>
            ) : softBlockWords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No soft block words added yet. Add some words to automatically replace with dolphin emojis.
                </TableCell>
              </TableRow>
            ) : (
              softBlockWords.map((word) => (
                <TableRow key={word.id}>
                  <TableCell className="font-medium">{word.word}</TableCell>
                  <TableCell>{word.category || 'general'}</TableCell>
                  <TableCell>{new Date(word.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteWord(word.id)}
                      disabled={isDeleting === word.id}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
